<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title class="headline primary--text">
              <v-icon class="mr-2">mdi-card-account-details</v-icon>
              Memberships
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                elevation="2"
                small
                to="/memberships/create"
                class="mr-2"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add Membership
              </v-btn>
              <v-btn
                color="success"
                elevation="2"
                small
                @click="exportToExcel"
                :loading="exporting"
              >
                <v-icon left small>mdi-microsoft-excel</v-icon>
                Export
              </v-btn>
            </v-card-title>

            <v-card-text>
              <!-- Filters -->
              <v-row class="mb-4">
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="filters.search_keyword"
                    label="Search"
                    prepend-inner-icon="mdi-magnify"
                    solo
                    rounded
                    dense
                    clearable
                    @input="search"
                    @click:clear="clearSearch"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.user_id"
                    :items="users"
                    item-text="name"
                    item-value="id"
                    label="Filter by Employee"
                    solo
                    rounded
                    dense
                    clearable
                    @change="getMemberships"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.is_active"
                    :items="statusOptions"
                    label="Status"
                    solo
                    rounded
                    dense
                    clearable
                    @change="getMemberships"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="filters.year"
                    :items="yearOptions"
                    label="Filter by Year"
                    solo
                    rounded
                    dense
                    clearable
                    @change="getMemberships"
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Data Table -->
              <v-data-table
                :headers="headers"
                :items="memberships"
                :loading="loading"
                :server-items-length="totalItems"
                :page.sync="page"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{
                  itemsPerPageOptions: [10, 25, 50, 100]
                }"
                fixed-header
                height="285"
                class="elevation-1"
                @update:page="getMemberships"
                @update:items-per-page="getMemberships"
              >
                <!-- Employee Column -->
                <template v-slot:[`item.user`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2">
                      <v-img
                        v-if="item.user && item.user.image_path"
                        :src="getImageUrl(item.user.image_path)"
                        :alt="item.user.first_name"
                      ></v-img>
                      <v-icon v-else color="grey lighten-1">mdi-account</v-icon>
                    </v-avatar>
                    <div>
                      <div class="body-2 font-weight-medium">
                        {{ getUserName(item.user) }}
                      </div>
                      <div class="caption grey--text">
                        {{ item.user ? item.user.email : 'Organizational Membership' }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Association Column -->
                <template v-slot:[`item.association_name`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="blue">
                      <v-img
                        v-if="item.image_path"
                        :src="getImageUrl(item.image_path)"
                        :alt="item.association_name"
                      ></v-img>
                      <v-icon v-else color="white">mdi-card-account-details</v-icon>
                    </v-avatar>
                    <div>
                      <div class="body-2 font-weight-medium">
                        {{ item.association_name || 'No Association Specified' }}
                      </div>
                      <div class="caption grey--text">
                        ID: {{ item.membership_id || 'Not specified' }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Year Column -->
                <template v-slot:[`item.year`]="{ item }">
                  <v-chip
                    small
                    :color="getYearColor(item.year)"
                    text-color="white"
                  >
                    {{ item.year || 'N/A' }}
                  </v-chip>
                </template>

                <!-- Status Column -->
                <template v-slot:[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    text-color="white"
                  >
                    {{ item.is_active ? 'Active' : 'Inactive' }}
                  </v-chip>
                </template>

                <!-- Actions Column -->
                <template v-slot:[`item.actions`]="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        :to="`/memberships/${encryptIt(item.id)}`"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>
                  
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="confirmDelete(item)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </template>

                <!-- No data template -->
                <template v-slot:no-data>
                  <empty-state icon="mdi-card-account-details" title="No memberships found" message="Start by adding your first membership entry" />
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this membership?
          <br><strong>{{ selectedItem?.association_name || 'This membership' }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteMembership">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "MembershipsIndex",
  data() {
    return {
      loading: false,
      exporting: false,
      memberships: [],
      users: [],
      totalItems: 0,
      page: 1,
      itemsPerPage: 10,
      deleteDialog: false,
      selectedItem: null,
      
      // Filters
      filters: {
        search_keyword: "",
        user_id: null,
        is_active: null,
        year: null,
      },
      
      // Options
      statusOptions: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      yearOptions: [],
      
      // Table Headers
      headers: [
        { text: "Employee", value: "user", width: "200px" },
        { text: "Association", value: "association_name", width: "250px" },
        { text: "Year", value: "year", width: "100px" },
        { text: "Status", value: "is_active", width: "100px" },
        { text: "Actions", value: "actions", sortable: false, width: "100px" },
      ],
    };
  },

  mounted() {
    this.getMemberships();
    this.getUsers();
    this.generateYearOptions();
  },

  methods: {
    async getMemberships() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.itemsPerPage,
          ...this.filters,
        };

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });

        const response = await axios.get("/memberships", { params });
        
        this.memberships = response.data.data;
        this.totalItems = response.data.count;
      } catch (error) {
        console.error("Error fetching memberships:", error);
        this.$toast.error("Failed to load memberships");
      } finally {
        this.loading = false;
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("/users", {
          params: { rowsPerPage: 1000 }
        });
        
        this.users = response.data.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },

    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 10;
      const endYear = currentYear + 1;
      
      this.yearOptions = [];
      for (let year = endYear; year >= startYear; year--) {
        this.yearOptions.push({
          text: year.toString(),
          value: year
        });
      }
    },

    search() {
      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.getMemberships();
      }, 500);
    },

    clearSearch() {
      this.filters.search_keyword = "";
      this.page = 1;
      this.getMemberships();
    },

    confirmDelete(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },

    async deleteMembership() {
      try {
        await axios.post(`/memberships/delete/${this.selectedItem.id}`);
        this.$toast.success("Membership deleted successfully");
        this.deleteDialog = false;
        this.selectedItem = null;
        this.getMemberships();
      } catch (error) {
        console.error("Error deleting membership:", error);
        this.$toast.error("Failed to delete membership");
      }
    },

    async exportToExcel() {
      this.exporting = true;
      try {
        const params = { ...this.filters };
        
        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === null || params[key] === undefined || params[key] === '') {
            delete params[key];
          }
        });

        const response = await axios.get("/memberships/excel", {
          params,
          responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `memberships_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$toast.success("Excel file downloaded successfully");
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.$toast.error("Failed to export data");
      } finally {
        this.exporting = false;
      }
    },

    getUserName(user) {
      if (!user) return 'N/A';
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'N/A';
    },

    getImageUrl(imagePath) {
      return this.mediaUrl + '/' + imagePath;
    },

    getYearColor(year) {
      if (!year) return 'grey';
      const currentYear = new Date().getFullYear();
      if (year === currentYear) return 'success';
      if (year > currentYear) return 'info';
      if (year >= currentYear - 2) return 'warning';
      return 'error';
    },
  },

  watch: {
    page() {
      this.getMemberships();
    },
    itemsPerPage() {
      this.page = 1;
      this.getMemberships();
    }
  }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.v-data-table >>> .v-data-table-header {
  background-color: #f5f5f5;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(33, 150, 243, 0.04) !important;
}
</style>